import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { SentimentDataPoint } from "@/types/index";

interface CurrentSentimentCardProps {
  sentimentData: SentimentDataPoint[];
  totalReviews: number;
}

export function CurrentSentimentCard({
  sentimentData,
  totalReviews
}: CurrentSentimentCardProps) {
  // Find the dominant sentiment
  const dominantSentiment = sentimentData.reduce(
    (max, current) => (current.value > max.value ? current : max),
    sentimentData[0] || { name: "Neutral", value: 0, color: "#94a3b8" }
  );

  // Calculate percentage
  const percentage = totalReviews > 0
    ? Math.round((dominantSentiment.value / totalReviews) * 100)
    : 0;

  // Get color based on sentiment
  const getSentimentColor = () => {
    switch (dominantSentiment.name.toLowerCase()) {
      case "positive":
        return "text-green-600 dark:text-green-400";
      case "negative":
        return "text-red-600 dark:text-red-400";
      default:
        return "text-gray-600 dark:text-gray-400";
    }
  };

  if (totalReviews === 0) {
    return (
      <Card className="w-full">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
          <CardTitle className="text-sm font-medium">
            Current Sentiment
          </CardTitle>
        </CardHeader>
        <CardContent className="pb-4">
          <p className="text-sm text-muted-foreground">
            No reviews yet to analyze sentiment.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
        <CardTitle className="text-sm font-medium">
          Current Sentiment
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 pb-4">
        {/* Main sentiment display */}
        <div className="flex flex-col items-center text-center space-y-1">
          <div className={`text-3xl font-bold ${getSentimentColor()}`}>
            {percentage}%
          </div>
          <div className="text-xs text-muted-foreground">
            {dominantSentiment.name} ({dominantSentiment.value} of {totalReviews} reviews)
          </div>
        </div>

        {/* Sentiment breakdown */}
        <div className="space-y-1.5 pt-2 border-t">
          {sentimentData.map((sentiment) => (
            <div key={sentiment.name} className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: sentiment.color }}
                />
                <span>{sentiment.name}</span>
              </div>
              <span className="font-medium">{sentiment.value}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
